const MOVE_WELL = [
  {
    title: 'VMware-based workloads',
    body: "Lift-and-shift from on-prem vSphere or other VMware-based platforms onto CyCloud's VCF-native infrastructure. CyLogic handles the migration scoping and runs the platform; you keep your VM configurations and operational habits.",
  },
  {
    title: 'Predictable steady-state workloads',
    body: 'ERP systems, databases, file services, application servers where capacity is known 12 months out.',
  },
  {
    title: 'Data-movement-heavy workloads',
    body: 'Analytics pipelines, data lakes, and large dataset ingest / egress workloads where hyperscaler egress fees compound monthly.',
  },
  {
    title: 'Mission-grade applications',
    body: 'Uptime predictability and pricing predictability matter more than burst elasticity.',
  },
  {
    title: 'Compliance-bound workloads',
    body: 'CUI hosting, DFARS-bound applications, FedRAMP-aligned environments where boundary inheritance reduces ATO burden.',
  },
  {
    title: 'Regulated commercial workloads',
    body: "Banks, healthcare systems, energy and utilities, and regulated enterprises with HIPAA, PCI-DSS, GLBA, or NERC-CIP control requirements. CyLogic's compliance posture (FedRAMP-aligned controls, IL5-aligned) maps directly onto commercial regulated workloads — same boundary, same operator, same predictable cost.",
  },
]

const HYPERSCALER_WINS = [
  {
    title: 'Elastic burst',
    body: 'Spinning up thousands of VMs in minutes for short-duration burst — large-scale parallel batch processing, traffic-spike autoscaling, ephemeral high-concurrency analytics.',
  },
  {
    title: 'Short-burst GPU at scale',
    body: 'Ephemeral training or inference jobs that need fleet-class GPU capacity for hours, not months.',
  },
  {
    title: 'Deep hyperscaler-service dependency',
    body: 'Applications that lean on 30+ hyperscaler-native managed services (Lambda, SQS, Step Functions, Bedrock equivalents) in one stack.',
  },
]

export function WorkloadFitness() {
  return (
    <section className="bg-chalk py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <header className="max-w-3xl mb-12">
          <h2 className="text-3xl sm:text-4xl font-semibold text-navy tracking-tight">
            Workloads that move well to CyCloud, where hyperscaler is the right answer, and
            where the build-your-own federal cloud path costs more than it saves.
          </h2>
          <p className="mt-3 text-base text-navy/70">
            Naming the workloads where we don't fit is a credibility move — capture engineers
            and CIOs both respect a vendor that names its own ceiling.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h3 className="font-data text-xs uppercase tracking-wide text-mint font-medium mb-4">
              Workloads that move well
            </h3>
            <div className="space-y-3">
              {MOVE_WELL.map((w) => (
                <FitnessCard key={w.title} title={w.title} body={w.body} />
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-data text-xs uppercase tracking-wide text-navy/55 font-medium mb-4">
              When hyperscaler is the right answer
            </h3>
            <div className="space-y-3">
              {HYPERSCALER_WINS.map((w) => (
                <FitnessCard key={w.title} title={w.title} body={w.body} muted />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function FitnessCard({ title, body, muted = false }: { title: string; body: string; muted?: boolean }) {
  return (
    <article
      className={`rounded-card border p-5 ${muted ? 'bg-white border-mist' : 'bg-white border-mint/20'}`}
    >
      <h4 className="text-base font-semibold text-navy">{title}</h4>
      <p className="mt-2 text-sm text-navy/70 leading-relaxed">{body}</p>
    </article>
  )
}
